import React from "react";
import { useTranslation } from "react-i18next";
import { AuthRedirectProps } from "@/services/auth/AuthRedirect";
import ServiceAction from "@/pages/settings/components/connectedAccounts/ServiceAction";
import {
  Google,
  GoogleDrive,
  Microsoft,
  Slack,
  Zoom,
  ZoomInfo
} from "@dreamhub/designsystem/icons";
import {
  useDriveConnectorStatus,
  useSettingsTokens
} from "@/entities/settings/hooks";
import {
  REGISTER_SERVICES,
  Service
} from "@/pages/settings/components/connectedAccounts/types";
import {
  canDisconnectService,
  getServiceConnection
} from "@/pages/settings/components/connectedAccounts/serviceConnection";
import PageLayout from "@/pages/settings/components/PageLayout";
import { SectionProps } from "@/pages/settings/components/sections/Section";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import {
  INTEGRATIONS,
  hasServiceToken
} from "@/pages/settings/components/integrations/integrations";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useCanAccessAdminSettings } from "@/pages/settings/hooks";

interface ConnectedAccountsProps {
  onAuthRedirect: (props: AuthRedirectProps) => void;
  /** Services listed on the Integrations page are managed there instead. */
  hideIntegrations?: boolean;
}

const ConnectedAccounts: React.FC<ConnectedAccountsProps> = ({
  onAuthRedirect,
  hideIntegrations = false
}) => {
  const { t } = useTranslation();
  const isSettingsAdmin = useCanAccessAdminSettings();
  const {
    data: tokensData,
    isLoading: isTokensLoading,
    isError: isTokensError
  } = useSettingsTokens();
  const { data: driveStatus } = useDriveConnectorStatus();
  const driveOAuthConfigured = driveStatus?.oauthConfigured ?? false;
  const { enabled: showZoomInfo } = useFeatureFlag(
    FeatureFlags.CRM_9112_ZOOMINFO_INTEGRATION
  );

  const services = [
    {
      name: t(`connectedAccounts.serviceNames.${Service.GOOGLE}`),
      description: t(
        `connectedAccounts.servicesDescriptions.${Service.GOOGLE}`
      ),
      icon: <Google />,
      serviceId: Service.GOOGLE,
      registerTo: "registerGoogleEmail",
      registerServices: REGISTER_SERVICES
    },
    {
      name: t(`connectedAccounts.serviceNames.${Service.MICROSOFT}`),
      description: t(
        `connectedAccounts.servicesDescriptions.${Service.MICROSOFT}`
      ),
      icon: <Microsoft />,
      serviceId: Service.MICROSOFT,
      registerTo: "registerMicrosoftEmail",
      registerServices: REGISTER_SERVICES
    },
    {
      name: t(`connectedAccounts.serviceNames.${Service.ZOOM}`),
      description: t(`connectedAccounts.servicesDescriptions.${Service.ZOOM}`),
      icon: <Zoom />,
      serviceId: Service.ZOOM,
      registerTo: "registerZoom"
    },
    {
      name: t(`connectedAccounts.serviceNames.${Service.SLACK}`),
      description: t(`connectedAccounts.servicesDescriptions.${Service.SLACK}`),
      icon: <Slack />,
      serviceId: Service.SLACK,
      registerTo: "registerSlack"
    },
    ...(showZoomInfo
      ? [
          {
            name: t(`connectedAccounts.serviceNames.${Service.ZOOMINFO}`),
            description: t(
              `connectedAccounts.servicesDescriptions.${Service.ZOOMINFO}`
            ),
            icon: <ZoomInfo />,
            serviceId: Service.ZOOMINFO,
            registerTo: "registerZoomInfo"
          }
        ]
      : []),
    ...(driveOAuthConfigured
      ? [
          {
            name: t(`connectedAccounts.serviceNames.${Service.GOOGLE_DRIVE}`),
            description: t(
              `connectedAccounts.servicesDescriptions.${Service.GOOGLE_DRIVE}`
            ),
            icon: <GoogleDrive />,
            serviceId: Service.GOOGLE_DRIVE,
            registerTo: "registerGoogleDrive"
          }
        ]
      : [])
  ];

  const visibleServices = hideIntegrations
    ? services.filter(
        (service) =>
          !INTEGRATIONS.some(
            (integration) =>
              hasServiceToken(integration) &&
              integration.serviceId === service.serviceId
          )
      )
    : services;

  const sections: SectionProps[] = visibleServices.map((service) => {
    const { hasTokens, allTokensValid } = getServiceConnection(
      tokensData?.tokens,
      service.serviceId
    );
    // This page is open to everyone, so connecting an account service is held
    // to the same Settings admin rule as disconnecting it.
    const adminOnly = !canDisconnectService(service.serviceId, isSettingsAdmin);

    const item: SettingItemLayoutProps = {
      title: service.name,
      subTitle: service.description,
      icon: (
        <LogoIconContainer
          index={-1}
          className="w-10 h-10"
          icon={service.icon}
          iconSize="md"
        />
      ),
      customAction: (
        <ServiceAction
          name={service.name}
          serviceId={service.serviceId}
          onAuthRedirect={(props) =>
            onAuthRedirect({
              ...props,
              redirectTo: "/settings/connectedAccounts"
            })
          }
          registerTo={service.registerTo}
          registerServices={service.registerServices}
          hasTokens={hasTokens}
          allTokensValid={allTokensValid}
          isTokenLoading={isTokensLoading}
          isTokenError={isTokensError}
          disableServiceDisconnection={adminOnly}
          disableConnect={adminOnly}
          disableConnectTooltip={
            adminOnly
              ? t("connectedAccounts.connectTooltip", { service: service.name })
              : undefined
          }
        />
      )
    };

    return {
      items: [item]
    };
  });

  return (
    <PageLayout
      data-test="settings-connected-accounts-page"
      title={t("connectedAccounts.title")}
      subTitle={t("connectedAccounts.description")}
      sections={sections}
    />
  );
};

export default ConnectedAccounts;
